import type { TableHeaderData } from '@forge/common';
import { WorkflowStatus, SortBy, SortOrder, IActivityItem, IUserDisplayName, IActivityDetails, IFilterOptions, IAccessibleCustomerOption, IAccessibleCustomersCacheEntry } from './Redux/activityModels';
import { validate as isUuid, NIL as EMPTY_GUID } from 'uuid';
export type BadgeDisplayType = 'error' | 'progress' | 'success' | 'warning' | 'neutral';

export const SORT_DIRECTION = {
  Asc: 'asc',
  Desc: 'desc',
} as const;

export type SortDirection = typeof SORT_DIRECTION[keyof typeof SORT_DIRECTION] | undefined;

export const STATUS_CONFIG: Record<WorkflowStatus, { badgeType: BadgeDisplayType; labelKey: string; className: string }> = {
  [WorkflowStatus.Success]:    { badgeType: 'success',  labelKey: 'activityLogStatusSuccess',  className: 'activity-status--success' },
  [WorkflowStatus.Failed]:     { badgeType: 'error',    labelKey: 'activityLogStatusFailed',    className: 'activity-status--failed' },
  [WorkflowStatus.InProgress]: { badgeType: 'progress', labelKey: 'activityLogStatusInProgress', className: 'activity-status--inprogress' },
  [WorkflowStatus.Queued]:     { badgeType: 'neutral',  labelKey: 'activityLogStatusQueued',    className: 'activity-status--queued' },
  [WorkflowStatus.Pending]:    { badgeType: 'neutral',  labelKey: 'activityLogStatusPending',   className: 'activity-status--pending' },
};

export const getStatusBadgeType = (status: string): BadgeDisplayType =>
  STATUS_CONFIG[status as WorkflowStatus]?.badgeType ?? 'neutral';

export const getStatusLabelKey = (status: string): string =>
  STATUS_CONFIG[status as WorkflowStatus]?.labelKey ?? status;

export const getStatusClassName = (status: string): string =>
  STATUS_CONFIG[status as WorkflowStatus]?.className ?? '';

export const normalizeStatus = (s?: string): WorkflowStatus => {
  switch (s?.toLowerCase()) {
    case 'success':
    case 'completed':    return WorkflowStatus.Success;
    case 'failed':       return WorkflowStatus.Failed;
    case 'inprogress':   return WorkflowStatus.InProgress;
    case 'queued':       return WorkflowStatus.Queued;
    case 'pending':      return WorkflowStatus.Pending;
    default:             return WorkflowStatus.Pending;
  }
};

type ColumnConfig = {
  id: string;
  labelKey: string;
  canSort: boolean;
};

const COLUMN_CONFIG: ColumnConfig[] = [
  { id: SortBy.UpdatedAt,  labelKey: 'activityLogLastUpdated', canSort: true },
  { id: SortBy.CreatedAt,  labelKey: 'activityLogCreatedOn',  canSort: true },
  { id: SortBy.Description, labelKey: 'activityLogActivity',  canSort: false },
  { id: 'customerName',    labelKey: 'customer',              canSort: false },
  { id: 'siteName',        labelKey: 'activityLogSite',       canSort: false },
  { id: SortBy.Status,     labelKey: 'activityLogStatus',     canSort: true },
  { id: 'createdBy',       labelKey: 'activityLogCreatedBy',  canSort: false },
  { id: 'updatedBy',       labelKey: 'activityLogUpdatedBy',  canSort: false },
  { id: 'details',         labelKey: 'activityLogDetails',    canSort: false },
];

export const buildHeaderData = (
  sortBy: SortBy,
  sortOrder: SortOrder,
  t: (key: string) => string,
  visibleColumnIds?: string[],
): TableHeaderData[] => {
  const columns = visibleColumnIds
    ? visibleColumnIds
        .map(id => COLUMN_CONFIG.find(c => c.id === id))
        .filter((c): c is ColumnConfig => c !== undefined)
    : COLUMN_CONFIG;

  // Calculate equal width for all visible columns
  const colCount = columns.length;
  const widthPercent = colCount > 0 ? `${(100 / colCount).toFixed(2)}%` : 'auto';
  const minWidth = 120; 
  const maxWidth = 400; 

  return columns.map(({ id, labelKey, canSort }) => ({
    id,
    content: t(labelKey),
    canSort,
    canResize: true,
    style: { width: widthPercent, minWidth, maxWidth },
    ...(canSort && { sort: id === sortBy ? sortOrder : undefined }),
  }));
};

export const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export const getPaginationInfoLabel = (
  currentPage: number,
  pageSize: number,
  totalItems: number,
  t: (key: string) => string,
): string => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(startItem + pageSize - 1, totalItems);
  return t('activityLogPaginationInfo')
    .replace('{startItem}', startItem.toString())
    .replace('{endItem}', endItem.toString())
    .replace('{totalItems}', totalItems.toString());
};

export const isValidUserId = (id: string | null | undefined): id is string => {
  if (!id) return false;
  const trimmed = id.trim();
  if (!trimmed || trimmed === EMPTY_GUID) return false;
  return isUuid(trimmed);
};

export const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

// Drops null/invalid entries and de-dupes by id so the UI never renders
// blank rows and select-all/apply never operate on unusable ids.
export const sanitizeById = <T extends { id?: unknown }>(
  items: T[] | undefined,
  getLabel: (item: T) => unknown
): T[] => {
  const seenIds = new Set<string>();
  return (items ?? []).filter((item): item is T => {
    if (!item || !isNonEmptyString(item.id) || !isNonEmptyString(getLabel(item)) || seenIds.has(item.id as string)) {
      return false;
    }
    seenIds.add(item.id as string);
    return true;
  });
};

/**
 * Collects unique user IDs from createdBy/updatedBy across all items,
 * excluding IDs that are already in the resolved cache.
 */
export const collectNewUserIds = (
  items: IActivityItem[],
  alreadyResolved: Record<string, string>
): string[] =>
  Array.from(
    new Set(
      items
        .flatMap(item => [item.createdBy, item.updatedBy])
        .filter(
          (id): id is string =>
            typeof id === 'string' &&
            isValidUserId(id) &&
            !(id in alreadyResolved)
        )
    )
  );

/**
 * Builds the displayName map from API resources.
 * Resolved names are populated first; any requested ID not returned
 * by the API is negative-cached as '' to prevent re-requesting it.
 */
/**
 * Returns the user IDs from filter options that have not yet been resolved,
 * so the saga can decide whether an API call is needed without inline guards.
 */
export const collectUncachedFilterUserIds = (
  filterOptions: IFilterOptions | null,
  alreadyResolved: Record<string, string>
): string[] => {
  const userIds = filterOptions?.createdByIds ?? [];
  return userIds.filter(id => typeof id === 'string' && isValidUserId(id) && !(id in alreadyResolved));
};

export const buildDisplayNameMap = (
  requestedIds: string[],
  resources: IUserDisplayName[]
): Record<string, string> => {
  const map: Record<string, string> = {};
  resources.forEach(user => {
    map[user.id] = user.displayName;
  });
  requestedIds.forEach(id => {
    if (!(id in map)) {
      map[id] = '';
    }
  });
  return map;
};

/**
 * Parses a details value that may arrive as an object or a JSON string.
 * Returns the parsed IActivityDetails, or null if absent / malformed.
 */
export const parseActivityDetails = (details: IActivityDetails | string | null | undefined): IActivityDetails | null => {
  if (details == null) return null;
  if (typeof details === 'string') {
    try { return JSON.parse(details) as IActivityDetails; } catch { return null; }
  }
  return details;
};

export const mapAccessibleCustomerOptions = (
  customers: IAccessibleCustomerOption[] | undefined
): IAccessibleCustomerOption[] =>
  (customers ?? []).map(customer => ({ id: customer.id, name: customer.name }));

export const isAccessibleCustomersCacheFresh = (
  cacheEntry: IAccessibleCustomersCacheEntry | undefined,
  ttlMs: number,
  now = Date.now()
): boolean => Boolean(cacheEntry && now - cacheEntry.fetchedAt < ttlMs);

export const filterCustomerOptions = (
  customerOptions: IAccessibleCustomerOption[],
  searchTerm: string
): IAccessibleCustomerOption[] => {
  const normalizedTerm = searchTerm.trim().toLowerCase();
  if (!normalizedTerm) {
    return customerOptions;
  }

  return customerOptions.filter(option => {
    const name = typeof option?.name === 'string' ? option.name : '';
    return name.toLowerCase().includes(normalizedTerm);
  });
};

export const buildVisibleCustomerOptions = (
  customerOptions: IAccessibleCustomerOption[],
  visibleOptionsCount: number,
  selectedCustomerId?: string,
  hasSearchQuery = false
): IAccessibleCustomerOption[] => {
  const baseOptions = customerOptions.slice(0, visibleOptionsCount);

  if (hasSearchQuery || !selectedCustomerId) {
    return baseOptions;
  }

  const selectedOption = customerOptions.find(option => option.id === selectedCustomerId);
  if (!selectedOption) {
    return baseOptions;
  }

  const optionsWithoutSelected = baseOptions.filter(option => option.id !== selectedCustomerId);
  return [selectedOption, ...optionsWithoutSelected].slice(0, visibleOptionsCount);
};

export const getRemainingCustomerOptionsCount = (
  totalOptionsCount: number,
  visibleOptionsCount: number
): number => Math.max(0, totalOptionsCount - visibleOptionsCount);

export const ACTIVITY_SEARCH_DEBOUNCE_MS = 300;
export const ACTIVITY_SCROLL_LOAD_THRESHOLD_PX = 50;
export const ACTIVITY_FILTER_SEARCH_INPUT_THRESHOLD = 5;
export const ACTIVITY_FILTER_LIST_MAX_HEIGHT_PX = 300;

export const isNearBottom = (
  scrollTop: number,
  clientHeight: number,
  scrollHeight: number,
  thresholdPx: number = ACTIVITY_SCROLL_LOAD_THRESHOLD_PX
): boolean => scrollTop + clientHeight >= scrollHeight - thresholdPx;

export type IFilterCheckboxOption = {
  id: string;
  label: string;
};

export const filterCheckboxOptions = (
  options: IFilterCheckboxOption[],
  searchTerm: string
): IFilterCheckboxOption[] => {
  const normalizedTerm = searchTerm.trim().toLowerCase();
  if (!normalizedTerm) {
    return options;
  }

  return options.filter(option => {
    const label = typeof option?.label === 'string' ? option.label : '';
    return label.toLowerCase().includes(normalizedTerm);
  });
};

export const getNextVisibleCount = (
  currentVisibleCount: number,
  pageSize: number,
  totalItemsCount: number
): number => Math.min(currentVisibleCount + pageSize, totalItemsCount);

/**
 * Converts the modifiedData array of an IActivityDetails object into a
 * human-readable comma-separated string, e.g. "Name: John, Email: test@example.com".
 * Returns an empty string when there is no data.
 * Field names are translated using the provided translation function.
 */
export const formatModifiedDataText = (
  details: IActivityDetails | string | null | undefined,
  t: (key: string) => string
): string => {
  const parsed = parseActivityDetails(details);
  const modifiedData = parsed?.modifiedData;
  if (!Array.isArray(modifiedData) || modifiedData.length === 0) return '';
  const text = modifiedData
    .map(({ field, value }) => {
      const translatedField = translateFieldName(field, t);
      return value != null ? `${translatedField}: ${value}` : translatedField;
    })
    .filter(s => s.trim() !== '')
    .join(', ');
  return text;
};

export const ACTIVITY_TOAST_MESSAGES = {
  retryError:         'activityLogRetryError',
  retrySuccess:       'activityLogRetrySuccess',
  retryAllFailed:     'activityLogRetryAllFailed',
  retryPartialFailed: 'activityLogRetryPartialFailed',
  retryAllSuccess:    'activityLogRetryAllSuccess',
  userNamesError:     'activityLogUserNamesError',
} as const;

export const WORKFLOW_TASK_TRANSLATION_MAP: Record<string, string> = {
  // Workflow names (parent-level)
  'Customer Creation': 'workflowCustomerCreation',
  'Customer Delete': 'workflowCustomerDelete',
  'Customer Update': 'workflowCustomerUpdate',
  'File attachments scan status': 'workflowFileAttachmentsScan',
  'Site Creation': 'workflowSiteCreation',
  'Site Delete': 'workflowSiteDelete',
  'Site Update': 'workflowSiteUpdate',
  'Subscription Association': 'workflowSubscriptionAssociation',
  'Add User': 'workflowAddUser',
  'Workflow to delete user': 'workflowDeleteUser',
  'Forge API Onboarding': 'workflowForgeAPIOnboarding',
  'Workflow to add user Multiple': 'workflowAddUserMultiple',
  'Workflow to Delete user Multiple': 'workflowDeleteUserMultiple',
  'Model File Upload': 'workflowModelFileUpload',
  'Gateway Association': 'workflowGatewayAssociation',
  'Name Plate': 'workflowNamePlate',
  // Task descriptions (task-level)
  'CB Customer Creation': 'workflowTaskCBCustomerCreation',
  'Onboarding status update in partner portal': 'workflowTaskOnboardingStatusUpdatePartnerPortal',
  'Update CB Customer Id in partner portal': 'workflowTaskUpdateCBCustomerIdPartnerPortal',
  'EOM Customer Creation': 'workflowTaskEOMCustomerCreation',
  'Onboard customer for AI': 'workflowTaskOnboardCustomerForAI',
  'Delete Customer in CB Portal': 'workflowTaskDeleteCustomerInCBPortal',
  'Update Customer in CB Portal': 'workflowTaskUpdateCustomerInCBPortal',
  'Update Customer in EOM': 'workflowTaskUpdateCustomerInEOM',
  'Update Customer in asset modelling': 'workflowTaskUpdateCustomerInAssetModelling',
  'Track scan status of file attachment': 'workflowTaskTrackScanStatusFileAttachment',
  'CB Site Creation': 'workflowTaskCBSiteCreation',
  'Update CB Site Id in partner portal': 'workflowTaskUpdateCBSiteIdPartnerPortal',
  'EOM Site Creation': 'workflowTaskEOMSiteCreation',
  'Update EOM Site Id in partner portal': 'workflowTaskUpdateEOMSiteIdPartnerPortal',
  'Sync existing users to CB Portal': 'workflowTaskSyncExistingUsersCBPortal',
  'Update user sync status in partner portal': 'workflowTaskUpdateUserSyncStatusPartnerPortal',
  'Calculate Energy Savings Potential': 'workflowTaskCalculateEnergySavingsPotential',
  'Delete Site in CB Portal': 'workflowTaskDeleteSiteInCBPortal',
  'Delete Site in EOM': 'workflowTaskDeleteSiteInEOM',
  'Update Site in CB Portal': 'workflowTaskUpdateSiteInCBPortal',
  'Update Site in EOM': 'workflowTaskUpdateSiteInEOM',
  'Update Site in asset modelling': 'workflowTaskUpdateSiteInAssetModelling',
  'Update Site in Find API': 'workflowTaskUpdateSiteInFindAPI',
  'Sync subscription association to CB Portal': 'workflowTaskSyncSubscriptionAssociationCBPortal',
  'Enable Brick ontology': 'workflowTaskEnableBrickOntology',
  'Onboard Customer to ForgeAPI': 'workflowTaskOnboardCustomerForgeAPI',
  'Publish ForgeAPI onboarding status to Credential Management': 'workflowTaskPublishForgeAPIOnboardingStatus',
  'Add user in CB portal': 'workflowTaskAddUserInCBPortal',
  'Update user provisioning status': 'workflowTaskUpdateUserProvisioningStatus',
  'Delete user in CB portal': 'workflowTaskDeleteUserInCBPortal',
  'Update user deletion status': 'workflowTaskUpdateUserDeletionStatus',
  'Forgeapi Onboarding status': 'workflowTaskForgeAPIOnboardingStatus',
  'Workflow to add user': 'workflowTaskAddUser',
  'Process model file': 'workflowTaskProcessModelFile',
  'Notify asset modelling': 'workflowTaskNotifyAssetModelling',
  'Associate gateway to site': 'workflowTaskAssociateGateway',
  'Fetch model to asset modelling': 'workflowTaskFetchModelToAssetModelling',
  'Process Nameplate File.': 'workflowTaskNameProcessNamePlate',
  'Update Model in Find API': 'workflowTaskUpdateModelInFindAPI',
};

const ITERATION_SUFFIX_REGEX = /\s*-\s*Iteration\s+(\d+)$/i;

export const getWorkflowTaskTranslationKey = (description: string): string => {
  if (WORKFLOW_TASK_TRANSLATION_MAP[description]) {
    return WORKFLOW_TASK_TRANSLATION_MAP[description];
  }
  
  const cleanedDescription = description.replace(ITERATION_SUFFIX_REGEX, '').trim();
  return WORKFLOW_TASK_TRANSLATION_MAP[cleanedDescription] || cleanedDescription;
};

export const translateWorkflowTaskDescription = (
  description: string | null | undefined,
  t: (key: string) => string
): string => {
  const iterationMatch = description?.match(ITERATION_SUFFIX_REGEX);
  const translationKey = getWorkflowTaskTranslationKey(description ?? '');
  const translatedBase = t(translationKey);
  
  if (iterationMatch) {
    const iterationNumber = iterationMatch[1];
    return `${translatedBase} - ${t('activityLogIteration')} ${iterationNumber}`;
  }
  
  return translatedBase;
};

export const ACTIVITY_FIELD_TRANSLATION_MAP: Record<string, string> = {
  'first name': 'activityLogFieldFirstName',
  'last name': 'activityLogFieldLastName',
  'subscription name': 'activityLogFieldSubscriptionName',
  name: 'activityLogFieldName',
  currency: 'activityLogFieldCurrency',
  zip: 'activityLogFieldZip',
  area: 'activityLogFieldArea',
  city: 'activityLogFieldCity',
  state: 'activityLogFieldState',
  country: 'activityLogFieldCountry',
  areaunit: 'activityLogFieldAreaUnit',
  language: 'activityLogFieldLanguage',
  latitude: 'activityLogFieldLatitude',
  sitetype: 'activityLogFieldSiteType',
  timezone: 'activityLogFieldTimezone',
  longitude: 'activityLogFieldLongitude',
  countrycode: 'activityLogFieldCountryCode',
  addressline1: 'activityLogFieldAddressLine1',
  addressline2: 'activityLogFieldAddressLine2',
  contactemail: 'activityLogFieldContactEmail',
  siteId: 'activityLogFieldSiteId',
};

export const translateFieldName = (field: string | null | undefined, t: (key: string) => string): string => {
  if (!field) return '';
  const translationKey = ACTIVITY_FIELD_TRANSLATION_MAP[field];
  return translationKey ? t(translationKey) : field;
};
